'use client';

import * as React from 'react';
import { capture } from '@tagadapay/analytics';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SETUP_MOTION } from '@/components/setup/experience/constants';
import { useSetupNavigation } from '@/components/setup/experience/hooks/useSetupNavigation';
import { useSetupProgress } from '@/components/setup/experience/hooks/useSetupProgress';
import {
  clampStep,
  getSetupStepIndex,
  getSetupStripDefinitions,
  stepToStripIndex,
  type SetupExperienceStage,
  type SetupStep,
} from '@/components/setup/experience/lib/setupSteps';
import { SetupShell } from '@/components/setup/experience/SetupShell';
import { SetupCheckoutStep } from '@/components/setup/experience/steps/SetupCheckoutStep';
import { SetupCompleteStep } from '@/components/setup/experience/steps/SetupCompleteStep';
import { SetupConnectShopifyStep } from '@/components/setup/experience/steps/SetupConnectShopifyStep';
import { SetupConnectWooStep } from '@/components/setup/experience/steps/SetupConnectWooStep';
import { SetupCreateStoreStep } from '@/components/setup/experience/steps/SetupCreateStoreStep';
import { SetupManualProductExperienceStep } from '@/components/setup/experience/steps/SetupManualProductExperienceStep';
import { SetupPathStep } from '@/components/setup/experience/steps/SetupPathStep';
import { SetupPaymentStep } from '@/components/setup/experience/steps/SetupPaymentStep';
import { SetupSessionRecovery } from '@/components/setup/experience/steps/SetupSessionRecovery';
import { SetupShippingStep } from '@/components/setup/experience/steps/SetupShippingStep';
import type { SetupManualCreatedStore } from '@/components/setup/setupManualTypes';
import { useOrgSlug } from '@/hooks/use-org-slug';
import { useIntegrationsList } from '@/lib/api/integrations';
import { useSyncShopifyProducts, useWoocommerceSyncProducts } from '@/lib/api/products';
import { trpc } from '@/lib/api/trpc';
import '@/styles/setup-experience-residual.css';

/** Polling cadence while waiting for the WooCommerce integration to appear server-side. */
const CONNECTION_PROBE_INTERVAL_MS = 2500;
/** Distinct negative probe results before concluding the merchant canceled the consent screen. */
const CONNECTION_PROBE_NEGATIVES_TO_CANCEL = 3;

export function SetupExperience() {
  const { urlSlug } = useOrgSlug();
  const { t } = useTranslation('setup');
  const nav = useSetupNavigation();
  const progress = useSetupProgress(nav.storeId);
  const utils = trpc.useUtils();
  const directionRef = React.useRef(1);
  const [oauthError, setOauthError] = React.useState<string | null>(null);

  const { mutate: startShopifyProductSync } = useSyncShopifyProducts();
  const { mutate: startWooProductSync } = useWoocommerceSyncProducts();

  const storeId = nav.storeId;
  const store = progress.store;
  const sourcePath = nav.source ?? progress.connectedProvider;
  const isDigital = progress.isDigital;
  // After products, physical stores configure shipping; digital stores have nothing to ship and
  // jump straight to payment. The clamp self-corrects if the store kind isn't loaded yet.
  const afterProductsStep: SetupStep = isDigital ? 'payment' : 'shipping';

  /** Dev/test helper: /org/:slug/setup?reset=1 wipes the cursor and restarts (dev only). */
  React.useEffect(() => {
    if (import.meta.env.DEV && nav.isResetRequested) nav.reset();
  }, [nav.isResetRequested, nav.reset]);

  /**
   * Shopify OAuth landing. The backend callback redirects to `…/setup?oauth=success&storeId=…`
   * (credentials are saved before the redirect, so the integration already exists server-side):
   * kick off the product import and advance. On error, drop the merchant back on the connect form.
   */
  const oauthHandledRef = React.useRef(false);
  React.useEffect(() => {
    if (oauthHandledRef.current || !nav.oauth) return;

    if (nav.oauth === 'success' && storeId) {
      // Wait until the store is loaded so `afterProductsStep` reflects the real store kind — a
      // digital store must land on payment, not shipping. The effect re-runs once `store` resolves.
      if (!store) return;
      oauthHandledRef.current = true;
      directionRef.current = 1;
      setOauthError(null);
      startShopifyProductSync({ storeId });
      // Await the integrations refresh before advancing so the DB-derived furthest step already
      // includes the new connection — navigating first could momentarily clamp back to products.
      void (async () => {
        await utils.integrations.list.invalidate({ storeId });
        nav.setParams({ oauth: null, message: null, shop: null, awaiting: null, step: afterProductsStep });
      })();
    } else if (nav.oauth === 'error') {
      oauthHandledRef.current = true;
      directionRef.current = -1;
      setOauthError(
        nav.oauthMessage ||
          (nav.source === 'woocommerce'
            ? t('experience.connect.wooOAuthGenericError')
            : t('experience.connect.shopifyOAuthGenericError')),
      );
      nav.setParams({
        source: nav.source === 'woocommerce' ? 'woocommerce' : 'shopify',
        sub: 'connect',
        step: 'products',
        oauth: null,
        message: null,
        shop: null,
        awaiting: null,
      });
    }
  }, [nav.oauth, nav.oauthMessage, storeId, store, afterProductsStep]);

  /**
   * WooCommerce return reconciliation. wc-auth returns no params and its callback is
   * server-to-server (can land AFTER the browser does), so we poll the integrations list — the
   * `?awaiting=woocommerce` marker comes back via the client-controlled return_url. A few negative
   * probes mean the merchant canceled (or hasn't finished); drop the marker and stop polling.
   */
  const isAwaitingWoo = nav.awaiting === 'woocommerce';
  const wooPoll = useIntegrationsList(isAwaitingWoo && storeId ? storeId : '', {
    refetchInterval: CONNECTION_PROBE_INTERVAL_MS,
  });
  const isWooConnected = Boolean(
    wooPoll.data?.some((integration) => integration.provider === 'woocommerce' && integration.enabled),
  );
  const wooSyncedRef = React.useRef(false);
  const wooProbeRef = React.useRef({ countedAt: 0, negatives: 0 });
  React.useEffect(() => {
    if (!isAwaitingWoo || !storeId) {
      wooProbeRef.current = { countedAt: 0, negatives: 0 };
      return;
    }
    if (isWooConnected) {
      // Wait until the store is loaded so we advance to the right step (digital skips shipping).
      if (!store) return;
      if (!wooSyncedRef.current) {
        wooSyncedRef.current = true;
        startWooProductSync({ storeId });
      }
      directionRef.current = 1;
      void utils.integrations.list.invalidate({ storeId });
      nav.setParams({ awaiting: null, oauth: null, message: null, shop: null, step: afterProductsStep });
      return;
    }
    // Count each settled probe — success OR error — so a transient fetch failure can't stall the
    // give-up logic into polling forever. After enough empty/failed probes, stop waiting.
    const settledAt = Math.max(wooPoll.dataUpdatedAt, wooPoll.errorUpdatedAt);
    if (settledAt && settledAt !== wooProbeRef.current.countedAt) {
      wooProbeRef.current.countedAt = settledAt;
      wooProbeRef.current.negatives += 1;
      if (wooProbeRef.current.negatives >= CONNECTION_PROBE_NEGATIVES_TO_CANCEL) {
        nav.clearOAuthParams();
      }
    }
  }, [isAwaitingWoo, isWooConnected, storeId, store, afterProductsStep, wooPoll.dataUpdatedAt, wooPoll.errorUpdatedAt]);

  // --- Navigation handlers (each forward step awaits its gating query so the clamp doesn't bounce) ---

  const handleStoreCreated = (created: SetupManualCreatedStore) => {
    directionRef.current = 1;
    nav.setStoreCreated(created.id);
  };

  const handleStoreUpdated = () => {
    directionRef.current = 1;
    nav.goToStep('products');
  };

  const handleSourceContinue = () => {
    if (!nav.source) return;
    directionRef.current = 1;
    nav.goToSub(nav.source === 'manual' ? 'manual_product' : 'connect');
  };

  const handleProductCreated = async () => {
    directionRef.current = 1;
    await utils.products.list.invalidate();
    nav.goToStep(afterProductsStep);
  };

  /** WooCommerce manual key entry connects synchronously — sync products and advance. */
  const handleWooManualConnected = async () => {
    if (!storeId) return;
    directionRef.current = 1;
    startWooProductSync({ storeId });
    await utils.integrations.list.invalidate({ storeId });
    nav.goToStep(afterProductsStep);
  };

  /** Already-connected platform (revisit): advance without re-syncing. */
  const handleConnectedContinue = () => {
    directionRef.current = 1;
    nav.goToStep(afterProductsStep);
  };

  const handleShippingContinue = async () => {
    directionRef.current = 1;
    await utils.shippingRates.list.invalidate();
    nav.goToStep('payment');
  };

  const handlePaymentContinue = async () => {
    if (!storeId) return;
    directionRef.current = 1;
    await utils.stores.getById.invalidate({ storeId });
    nav.goToStep('checkout');
  };

  const handleCheckoutLaunched = () => {
    void utils.funnels.list.invalidate();
  };

  const handleCheckoutComplete = () => {
    directionRef.current = 1;
    nav.goToStep('complete');
  };

  const goToStoreStep = () => {
    directionRef.current = -1;
    nav.goToStep('store');
  };

  const goToChooseSource = () => {
    directionRef.current = -1;
    nav.goToSub('choose_source');
  };

  /** Back into products — land on the sub-step that matches what the DB already holds. */
  const goBackToProducts = () => {
    directionRef.current = -1;
    if (progress.connectedProvider) {
      nav.setParams({ step: 'products', sub: 'connect', source: progress.connectedProvider });
    } else if (progress.milestones.hasProducts) {
      nav.setParams({ step: 'products', sub: 'manual_product', source: 'manual' });
    } else {
      nav.setParams({ step: 'products', sub: 'choose_source' });
    }
  };

  const goToShippingStep = () => {
    directionRef.current = -1;
    nav.goToStep('shipping');
  };

  const goToPaymentStep = () => {
    directionRef.current = -1;
    nav.goToStep('payment');
  };

  // --- Resolve which stage to render from the URL cursor, clamped to DB-derived progress ---

  const showRecovery = progress.storeMissing;
  const showLoader = Boolean(storeId) && progress.isLoading;
  const effectiveStep: SetupStep = store
    ? clampStep(nav.step, progress.furthestStep, progress.milestones.hasFunnel, isDigital)
    : 'store';

  // Fire once per DERIVED step actually rendered — not per forward-transition handler — so a
  // reload, browser back, or OAuth-return resume is captured the same as a forward click.
  // A missing `store` is NOT a reason to skip: the entry step renders before any store exists,
  // and gating on it would drop the funnel's largest cohort (arrived, never created a store).
  const lastSentStepRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (showRecovery || showLoader) return;
    if (lastSentStepRef.current === effectiveStep) return;
    lastSentStepRef.current = effectiveStep;
    capture('setup_step_viewed', {
      step_index: getSetupStepIndex(effectiveStep),
      step_id: effectiveStep,
      store_id: store?.id,
      is_digital: store ? isDigital : undefined,
    });
  }, [effectiveStep, store, showRecovery, showLoader, isDigital]);

  const productsSub = ((): 'choose_source' | 'connect' | 'manual_product' => {
    const sub = nav.sub ?? 'choose_source';
    if ((sub === 'connect' || sub === 'manual_product') && !sourcePath) return 'choose_source';
    return sub;
  })();

  const stage: SetupExperienceStage = showRecovery ? 'recover' : effectiveStep;
  const macroIndex = stepToStripIndex(
    showRecovery ? 'store' : store ? effectiveStep : (nav.step ?? 'store'),
    isDigital,
  );
  // Strip steps for the current store kind — physical keeps shipping (4), digital drops it (3).
  const macroSteps = getSetupStripDefinitions(isDigital);
  const stageKey = showRecovery
    ? 'recover'
    : showLoader
      ? 'loading'
      : effectiveStep === 'products'
        ? `products-${productsSub}`
        : effectiveStep;

  const renderProducts = () => {
    if (!store) return null;
    switch (productsSub) {
      case 'choose_source':
        return (
          <SetupPathStep
            selectedPath={sourcePath}
            onSelectPath={nav.setSource}
            onContinue={handleSourceContinue}
            onBack={goToStoreStep}
            canContinue={sourcePath !== null}
          />
        );
      case 'connect':
        if (sourcePath === 'shopify' && urlSlug) {
          return (
            <SetupConnectShopifyStep
              store={store}
              orgSlug={urlSlug}
              oauthError={oauthError}
              onContinue={handleConnectedContinue}
              onBack={goToChooseSource}
            />
          );
        }
        if (sourcePath === 'woocommerce' && urlSlug) {
          return (
            <SetupConnectWooStep
              store={store}
              orgSlug={urlSlug}
              oauthError={oauthError}
              onConnected={handleWooManualConnected}
              onContinue={handleConnectedContinue}
              onBack={goToChooseSource}
            />
          );
        }
        return null;
      case 'manual_product':
        return (
          <SetupManualProductExperienceStep
            store={store}
            onProductCreated={handleProductCreated}
            onBack={goToChooseSource}
          />
        );
      default:
        return null;
    }
  };

  const renderStage = () => {
    if (showRecovery) return <SetupSessionRecovery onRestart={nav.reset} />;
    if (showLoader) {
      return (
        <div className="flex min-h-[16rem] items-center justify-center">
          <Loader2 className="text-muted-foreground size-6 animate-spin" aria-hidden />
        </div>
      );
    }

    if (!store) {
      return <SetupCreateStoreStep onStoreCreated={handleStoreCreated} existingStore={null} />;
    }

    switch (effectiveStep) {
      case 'store':
        return (
          <SetupCreateStoreStep
            onStoreCreated={handleStoreCreated}
            existingStore={store}
            onStoreUpdated={handleStoreUpdated}
          />
        );

      case 'products':
        return renderProducts();

      case 'shipping':
        return (
          <SetupShippingStep
            storeId={store.id}
            baseCurrency={store.baseCurrency}
            onBack={goBackToProducts}
            onContinue={handleShippingContinue}
          />
        );

      case 'payment':
        return (
          <SetupPaymentStep
            storeId={store.id}
            storeName={store.name}
            baseCurrency={store.baseCurrency}
            onBack={isDigital ? goBackToProducts : goToShippingStep}
            onContinue={handlePaymentContinue}
          />
        );

      case 'checkout':
        return (
          <SetupCheckoutStep
            storeId={store.id}
            baseCurrency={store.baseCurrency}
            storeLogoUrl={store.logoUrl}
            storeName={store.name}
            storePrimaryColor={store.primaryColor}
            launchedFunnelId={progress.funnelId}
            connectedProvider={progress.connectedProvider}
            isDigital={isDigital}
            initialVertical={store.vertical}
            onBack={goToPaymentStep}
            onLaunched={handleCheckoutLaunched}
            onComplete={handleCheckoutComplete}
          />
        );

      case 'complete':
        if (!progress.funnelId) return null;
        return (
          <SetupCompleteStep
            orgSlug={urlSlug ?? ''}
            storeId={store.id}
            storeName={store.name}
            baseCurrency={store.baseCurrency}
            funnelId={progress.funnelId}
            sourcePath={sourcePath}
          />
        );

      default:
        return null;
    }
  };

  return (
    <SetupShell stage={stage} macroIndex={macroIndex} macroSteps={macroSteps}>
      <AnimatePresence mode="wait" custom={directionRef.current}>
        <motion.div
          key={stageKey}
          custom={directionRef.current}
          variants={SETUP_MOTION}
          initial="enter"
          animate="center"
          exit="exit"
          transition={SETUP_MOTION.transition}
          className="min-h-0 w-full"
        >
          {renderStage() ?? <SetupSessionRecovery onRestart={nav.reset} />}
        </motion.div>
      </AnimatePresence>
    </SetupShell>
  );
}
